// =============================================================================
// Helpers PUROS de autorizacion. Sin imports de runtime — corren igual en el
// Worker y en el browser, y son LA MISMA funcion en los dos lados: el dashboard
// oculta lo que el worker va a rechazar, sin duplicar la regla.
//
// INVARIANTE: el dashboard usa esto para decidir que MOSTRAR. El worker lo usa
// para decidir que PERMITIR. Ocultar un boton no es autorizar — cada endpoint
// aplica su propio `requirePermission`. Si alguna vez el unico control de un
// permiso es del lado cliente, es un bug de seguridad.
// =============================================================================

import { canPrintflowDo } from "./can-printflow-do";
import { PERMISSIONS, type PermissionsMap, RECURSOS_INTERNOS, type Recurso } from "./permissions";

/**
 * Indice codigo plano -> (recurso, accion).
 *
 * Existe porque los codigos de imprenta no se parten por el primer punto:
 * `roles.permisos.read` es recurso `roles` + accion `permisos.read`, y
 * `sistema.config.update` es recurso `sistema` + accion `config.update`. Un
 * `split(".")` ingenuo daria recurso `roles` accion `permisos` — un permiso que
 * no existe, y por lo tanto una denegacion silenciosa.
 */
const INDICE: Readonly<Record<string, { recurso: Recurso; accion: string }>> = Object.fromEntries(
  PERMISSIONS.map((p) => [p.codigo, { recurso: p.recurso, accion: p.accion }]),
);

/** `true` si el mapa de permisos habilita `recurso.accion`. */
export function can(permisos: PermissionsMap, recurso: Recurso, accion: string): boolean {
  return canPrintflowDo(permisos, { [recurso]: [accion] });
}

/**
 * Igual que `can` pero desde el codigo plano (`"presupuestos.aprobar"`).
 *
 * Devuelve `false` para codigos fuera del catalogo — fail-closed: un typo niega,
 * nunca concede.
 */
export function canCodigo(permisos: PermissionsMap, codigo: string): boolean {
  const entrada = Object.hasOwn(INDICE, codigo) ? INDICE[codigo] : undefined;
  if (!entrada) return false;
  return can(permisos, entrada.recurso, entrada.accion);
}

/** `true` si habilita AL MENOS UNO de los codigos. */
export function canAnyCodigo(permisos: PermissionsMap, ...codigos: string[]): boolean {
  return codigos.some((c) => canCodigo(permisos, c));
}

/** `true` si habilita TODOS los codigos. */
export function canAllCodigo(permisos: PermissionsMap, ...codigos: string[]): boolean {
  return codigos.every((c) => canCodigo(permisos, c));
}

/**
 * Quita los recursos internos del plugin de un mapa de permisos.
 *
 * `ac`, `member` e `invitation` existen porque Better Auth los exige para
 * autorizar sus propias operaciones (ADR 0018), pero no son parte del producto:
 * no estan en `PERMISSIONS`, no tienen etiqueta en la consola y no se pueden
 * asignar a un rol dinamico. Lo que el dashboard necesita saber ya se lo dicen
 * `roles.*` y `usuarios.*`.
 *
 * Se aplica al salir hacia el cliente, NUNCA al autorizar: el enforcement usa el
 * mapa completo.
 */
export function permisosDelProducto(permisos: PermissionsMap): PermissionsMap {
  const salida: PermissionsMap = {};
  for (const [recurso, acciones] of Object.entries(permisos)) {
    if (RECURSOS_INTERNOS.has(recurso)) continue;
    salida[recurso] = acciones;
  }
  return salida;
}
